import '.an{position:relative;}.ssr-plugin'
import '.h5{max-height:24rem;}.ssr-plugin'
import '.by{overflow-y:auto;}.ssr-plugin'
import '.h2{padding:0.75rem;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.q{flex-direction:row;}.ssr-plugin'
import '.e2{gap:0.75rem;}.ssr-plugin'
import '.r{align-items:center;}.ssr-plugin'
import '.av{border-radius:1.25rem;}.ssr-plugin'
import '.z{cursor:pointer;}.ssr-plugin'
import '.h6{transition:border-color 0.15s;}.ssr-plugin'
import '.l{width:100%;}.ssr-plugin'
import '.h7{margin:0px;}.ssr-plugin'
import '.d7{background:transparent;}.ssr-plugin'
import '.h8{font:inherit;}.ssr-plugin'
import '.bi{color:inherit;}.ssr-plugin'
import '.h9{text-align:left;}.ssr-plugin'
import { Alert } from '@app/components/core/alert/Alert'
import { Button } from '@app/components/core/button/Button'
import { Tag } from '@app/components/core/tag/Tag'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import useDateFormat from '@app/util/useDateFormat'
import {
  faLocationDot,
  faClock,
  faPenField,
  faUser,
  faHouse,
  faBuildingColumns,
  faCalendarClock,
  faCircleXmark,
  faTrowelBricks,
} from '@fortawesome/pro-light-svg-icons'
import { faPlus, faCheck, faShare } from '@fortawesome/pro-regular-svg-icons'
import { i18n } from '@i18n/index'
import { useLanguage } from '@i18n/useLanguage'
import { styled } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import { Card } from '@app/components/blocks/card/Card'
import useMediaQuery from '@app/util/useMediaQuery'
import { Modal } from '@app/components/blocks/modal/Modal'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { useState, useMemo, useCallback } from 'react'
import { navigate } from 'wouter/use-browser-location'
import { pages } from '@app/util/links'
import { ParagraphedText } from '@app/components/core/typography/ParagraphedText'
import { useAuthState, useClient, useQuery } from '@based/sdk/react'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { ViewPlotModel } from 'functions/app/plot/view'
import { UserAdminInitiative } from 'functions/app/plot/list-user-admin-initiatives'
import { addressBuilder } from '@app/util/helpers'
import {
  IconList,
  IconListItem,
} from '@app/components/blocks/iconList/IconList'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { RadioButtonGroup } from '@app/components/core/radioButton/RadioButtonGroup'
import defaultPicture from '@app/assets/defaultProjectPicture.png'
import { Icon } from '@app/components/core/icon/Icon'
import { Avatar } from '@app/components/core/avatar/Avatar'

type FollowModalStep = 'closed' | 'chooseType' | 'confirmIndividual'

type FollowType = 'initiative' | 'individual'

export const DataCard = ({ plot }: { plot: ViewPlotModel }) => {
  const isMobile = useMediaQuery()
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.plotPage
  const formater = useDateFormat()
  const [sharePlotModal, setSharePlotModal] = useState(false)
  const client = useClient()
  const { withAuthCheck, checkAuth } = useAuthModal()
  const authState = useAuthState()

  const isFollowingQuery = useQuery(authState?.userId ? 'is-following' : null, {
    entityType: 'plot',
    entityId: plot.id,
    userId: Number(authState?.userId),
  })
  const isFollowing = (isFollowingQuery.data as boolean) ?? false

  const [modalStep, setModalStep] = useState<FollowModalStep>('closed')
  const [followType, setFollowType] = useState<FollowType>('individual')
  const [selectedInitiativeIds, setSelectedInitiativeIds] = useState<
    Set<number>
  >(new Set())

  const userInitiativesQuery = useQuery(
    'list-user-admin-initiatives',
    !plot.byCrowdbuilding && authState.userId
      ? { userId: Number(authState.userId) }
      : null,
  )
  const userInitiatives: UserAdminInitiative[] = useMemo(
    () => userInitiativesQuery.data ?? [],
    [userInitiativesQuery.data],
  )

  const userFollowingInitiativeIds = useMemo(() => {
    const followingIds = new Set(
      (plot.followingInitiatives ?? []).map((i) => i.id),
    )
    return new Set(
      userInitiatives.filter((i) => followingIds.has(i.id)).map((i) => i.id),
    )
  }, [plot.followingInitiatives, userInitiatives])

  const location = addressBuilder({
    street: plot.location.address.street,
    city: plot.location.address.city,
  })
  const deadline = plot.applicationDeadline
    ? `${translations.deadline}: ${formater(new Date(plot.applicationDeadline))}`
    : null
  const numberOfUnits = translations.numberOfUnitsLabel.replace(
    '{0}',
    plot.numberOfUnits.toString(),
  )

  const applicationStatus = useMemo<
    { icon: IconProp; text: string } | undefined
  >(() => {
    if (plot.applicationStatus === 'applicationOpen') {
      return {
        icon: faPenField,
        text: translations.applicationStatus.applicationOpen,
      }
    } else if (plot.applicationStatus === 'applicationClosed') {
      return {
        icon: faCircleXmark,
        text: translations.applicationStatus.applicationClosed,
      }
    } else if (plot.applicationStatus === 'inPreparation') {
      return {
        icon: faCalendarClock,
        text: translations.applicationStatus.inPreparation,
      }
    }
    return undefined
  }, [plot.applicationStatus, translations.applicationStatus])

  const listItems = useMemo<IconListItem[]>(
    () => [
      { icon: faLocationDot, text: location },
      ...(deadline ? [{ icon: faClock, text: deadline }] : []),
      { icon: faHouse, text: numberOfUnits },
      ...(applicationStatus
        ? [{ icon: applicationStatus.icon, text: applicationStatus.text }]
        : []),
    ],
    [location, deadline, numberOfUnits, applicationStatus],
  )

  const hasInitiativesFollowing = userFollowingInitiativeIds.size > 0
  const isFollowingAnything = isFollowing || hasInitiativesFollowing
  const isFollowingBoth = isFollowing && hasInitiativesFollowing

  const toggleUserFollow = () => {
    void withAuthCheck(async () => {
      await client.call('follow-plot', { plotId: plot.id })
      setModalStep('closed')
    })
  }

  const onFollow = () => {
    if (!checkAuth()) {
      return
    }

    if (plot.byCrowdbuilding) {
      if (isFollowing) {
        toggleUserFollow()
      } else {
        setModalStep('confirmIndividual')
      }
      return
    }

    setFollowType('individual')
    setSelectedInitiativeIds(new Set(userFollowingInitiativeIds))

    if (isFollowingBoth) {
      setModalStep('chooseType')
    } else if (userInitiatives.length > 0) {
      setModalStep('chooseType')
    } else if (isFollowing) {
      toggleUserFollow()
    } else {
      setModalStep('confirmIndividual')
    }
  }

  const onShare = () => setSharePlotModal(true)

  const handleModalClose = () => {
    if (
      modalStep === 'confirmIndividual' &&
      userInitiatives.length > 0 &&
      !plot.byCrowdbuilding
    ) {
      setModalStep('chooseType')
      return
    }
    setModalStep('closed')
  }

  const handleModalConfirm = () => {
    switch (modalStep) {
      case 'chooseType':
        if (followType === 'individual') {
          if (isFollowing) {
            toggleUserFollow()
          } else {
            setModalStep('confirmIndividual')
          }
        } else {
          const idsToToggle: number[] = []
          for (const id of selectedInitiativeIds) {
            if (!userFollowingInitiativeIds.has(id)) {
              idsToToggle.push(id)
            }
          }
          for (const id of userFollowingInitiativeIds) {
            if (!selectedInitiativeIds.has(id)) {
              idsToToggle.push(id)
            }
          }
          if (idsToToggle.length === 0) {
            setModalStep('closed')
            return
          }
          void withAuthCheck(async () => {
            await client.call('follow-plot-initiative', {
              plotId: plot.id,
              initiativeIds: idsToToggle,
            })
            setModalStep('closed')
          })
        }
        break
      case 'confirmIndividual':
        toggleUserFollow()
        break
    }
  }

  const modalTitle = (() => {
    if (plot.byCrowdbuilding) {
      return undefined
    }
    if (isFollowingBoth) {
      return translations.unfollowModal.title
    }
    return translations.followModal.title
  })()

  const toggleInitiativeSelection = (id: number) => {
    setSelectedInitiativeIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <div className="an"
    >
      <Tag
        size="small"
        variant={plot.byCrowdbuilding ? 'accent' : 'tertiary'}
        label={
          plot.byCrowdbuilding
            ? translations.officialOffering
            : translations.privateOffering
        }
        iconLeft={plot.byCrowdbuilding ? faBuildingColumns : faUser}
        style={{
          position: 'absolute',
          right: '1rem',
          top: '0',
          zIndex: 1,
          transform: 'translateY(-50%)',
        }}
      />
      <Card
        style={{
          height: isMobile ? 'auto' : '27rem',
        }}
        contentStyle={{
          padding: '2.5rem 1.5rem 2rem 1.5rem',
        }}
      >
        <Column
          alignItems="stretch"
          style={{
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          <IconList items={listItems} />
          {plot.byCrowdbuilding ? (
            <OfficialCardContent
              plot={plot}
              isFollowing={isFollowing}
              onFollow={onFollow}
              onShare={onShare}
            />
          ) : (
            <PrivateCardContent
              isFollowing={isFollowingAnything}
              onFollow={onFollow}
              onShare={onShare}
            />
          )}
        </Column>
      </Card>

      <Modal
        isOpen={modalStep !== 'closed'}
        title={modalTitle}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
      >
        {modalStep === 'chooseType' && (
          <styled.div
            style={{
              width: isMobile ? undefined : '34rem',
              '& input[type="radio"]:checked': {
                background:
                  'radial-gradient(circle at center, white 0.25rem, var(--brand-secondary) 0.25rem)',
                borderColor: 'var(--brand-secondary)',
              },
            }}
          >
            <Column gap="1rem" alignItems="stretch">
              <Text.Medium>
                {isFollowingBoth
                  ? translations.unfollowModal.prompt
                  : translations.followModal.prompt}
              </Text.Medium>
              <RadioButtonGroup
                radioButtonStyle={{
                  fontSize: '1.125rem',
                }}
                options={[
                  {
                    value: 'initiative',
                    label: hasInitiativesFollowing
                      ? translations.unfollowModal.asInitiative
                      : translations.followModal.asInitiative,
                  },
                  {
                    value: 'individual',
                    label: isFollowing
                      ? translations.unfollowModal.asIndividual
                      : translations.followModal.asIndividual,
                  },
                ]}
                selectedOption={followType}
                onClick={(v) => setFollowType(v)}
              />
              {followType === 'initiative' && (
                <>
                  <Text.Medium>
                    {translations.followModal.selectInitiative}
                  </Text.Medium>
                  <div className="h5 by"
                  >
                    <Column gap="0.5rem" alignItems="stretch">
                      {userInitiatives.map((initiative) => (
                        <InitiativeSelectItem
                          key={initiative.id}
                          initiative={initiative}
                          selected={selectedInitiativeIds.has(initiative.id)}
                          onToggle={() =>
                            toggleInitiativeSelection(initiative.id)
                          }
                        />
                      ))}
                    </Column>
                  </div>
                </>
              )}
            </Column>
          </styled.div>
        )}

        {modalStep === 'confirmIndividual' && (
          <div style={{ minWidth: isMobile ? undefined : '28rem' }}>
            <Text.Medium style={{ lineHeight: '1.75rem' }}>
              <ParagraphedText>
                {plot.byCrowdbuilding
                  ? translations.modalCopy.official
                  : translations.modalCopy.nonOfficial}
              </ParagraphedText>
            </Text.Medium>
          </div>
        )}
      </Modal>

      <SocialShareModal
        isOpen={sharePlotModal}
        onClose={() => setSharePlotModal(false)}
        title={plot.name}
      />
    </div>
  )
}

const InitiativeSelectItem = ({
  initiative,
  selected,
  onToggle,
}: {
  initiative: UserAdminInitiative
  selected: boolean
  onToggle: () => void
}) => {
  const image = initiative.images?.[0]
  const municipality = initiative.location?.address?.municipality?.name
  const region = initiative.location?.address?.region?.name
  const locationParts = [municipality, region].filter(Boolean).join(', ')

  return (
    <styled.button className="h2 d q e2 r av z h6 l h7 d7 h8 bi h9"
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      style={{
        border: selected
          ? '1px solid var(--brand-secondary)'
          : '1px solid var(--gray-100)',
        '&:focus-visible': {
          outline: '2px solid var(--brand-secondary)',
          outlineOffset: '2px',
        },
      }}
    >
      <Avatar
        image={image ?? defaultPicture}
        style={{ width: '4.875rem', height: '4.875rem' }}
        shape="square"
      />
      <Column gap="0.125rem" style={{ flex: 1, minWidth: 0 }}>
        <Text.Medium
          isBold
          style={{ overflow: 'hidden', maxLines: 1, textOverflow: 'ellipsis' }}
        >
          {initiative.title}
        </Text.Medium>
        {initiative.developmentForm?.name && (
          <Text.Small
            style={{
              color: 'var(--palette-blue-500)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <Icon icon={faTrowelBricks} size="small" style={{ padding: 0 }} />{' '}
            {initiative.developmentForm?.name}
          </Text.Small>
        )}
        {locationParts && (
          <Text.Small
            style={{
              color: 'var(--palette-blue-500)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <Icon icon={faLocationDot} size="small" style={{ padding: 0 }} />{' '}
            {locationParts}
          </Text.Small>
        )}
      </Column>
    </styled.button>
  )
}

function plotHasActiveLinkedRegistryWindow(plot: ViewPlotModel): boolean {
  const registry = plot.plotRegistry
  if (!registry?.id) {
    return false
  }
  const windows = registry.applicationWindows ?? []
  const now = Date.now()
  return windows.some((w) => w.openDatetime <= now && w.closeDatetime >= now)
}

const OfficialCardContent = ({
  plot,
  isFollowing,
  onFollow,
  onShare,
}: {
  plot: ViewPlotModel
  isFollowing: boolean
  onFollow: () => void
  onShare: () => void
}) => {
  const isMobile = useMediaQuery()
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.plotPage

  const hasActiveRegistryWindow = useMemo(
    () => plotHasActiveLinkedRegistryWindow(plot),
    [plot],
  )

  const onRegisterInitiative = useCallback(() => {
    if (!hasActiveRegistryWindow || !plot.plotRegistry?.id) {
      return
    }
    navigate(
      `${pages.plotRegistry}?plotRegistryId=${String(plot.plotRegistry.id)}`,
    )
  }, [hasActiveRegistryWindow, plot.plotRegistry?.id])

  return (
    <Column gap="1rem" alignItems="stretch" style={{ flexGrow: 0 }}>
      <Alert variant="error" size="small" withIcon={false}>
        <Text.Medium color="var(--brand-primary)">
          {translations.plotRegistryRequirementAlert.split('{0}')[0]}
          <a
            href={pages.knowledgeHubModule('utrecht-inschrijven-kaartenbak')}
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {translations.plotRegistryRequirementAlertLinkText}
          </a>
          {translations.plotRegistryRequirementAlert.split('{0}')[1]}
        </Text.Medium>
      </Alert>
      <Button
        label={translations.registerInitiative}
        onClick={hasActiveRegistryWindow ? onRegisterInitiative : undefined}
        disabled={!hasActiveRegistryWindow}
      />
      <Row style={{ justifyContent: 'stretch' }}>
        <Button
          variant="secondary"
          label={translations.follow}
          icon={isFollowing ? faCheck : faPlus}
          iconPosition="left"
          size={isMobile ? 'small' : 'medium'}
          style={{ flex: '1 0 0' }}
          onClick={onFollow}
        />
        <Button
          variant="secondary"
          label={translations.share}
          icon={faShare}
          iconPosition="left"
          size={isMobile ? 'small' : 'medium'}
          style={{ flex: '1 0 0' }}
          onClick={onShare}
        />
      </Row>
    </Column>
  )
}

const PrivateCardContent = ({
  isFollowing,
  onFollow,
  onShare,
}: {
  isFollowing: boolean
  onFollow: () => void
  onShare: () => void
}) => {
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.plotPage
  return (
    <Column gap="1rem" alignItems="stretch" style={{ flexGrow: 0 }}>
      <Button
        label={translations.follow}
        icon={isFollowing ? faCheck : faPlus}
        iconPosition="left"
        onClick={onFollow}
      />
      <Button
        variant="secondary"
        label={translations.share}
        icon={faShare}
        iconPosition="left"
        onClick={onShare}
      />
    </Column>
  )
}
